import React from 'react';
import type { FC, ReactNode } from 'react';
import { Box, Text, Key, PointerSmallIcon } from '@wix/cli-ui-kit';
import { Trans } from '@wix/cli-i18n';
import { variant, onTerms, fields, match, payload } from 'variant';
import type { TypeNames, GVariantOf } from 'variant';

const ListItem: FC<{
  itemNumber: number;
  children: ReactNode;
}> = ({ itemNumber, children }) => {
  const keyValue = `${itemNumber}`;
  return (
    <Box flexDirection="row" gap={1}>
      <Text>
        <PointerSmallIcon /> <Key value={keyValue} /> -
      </Text>
      {children}
    </Box>
  );
};

export const NumericOption = variant(
  onTerms(({ I: _I, V }) => ({
    Item: fields<{ title: string; description?: string; value: typeof _I }>(),
    ViewMore: payload(V),
  }))
);

export type NumericOption<
  I,
  V,
  TType extends TypeNames<typeof NumericOption> = undefined,
> = GVariantOf<typeof NumericOption, TType, { I: I; V: V }>;

interface Props<I, V> {
  startIndex: number;
  titleKey: string;
  items: Array<NumericOption<I, V>>;
}

export function NumericViewer<I, V>({
  startIndex,
  titleKey,
  items,
}: Props<I, V>) {
  return (
    <Box flexDirection="column">
      <Trans i18nKey={titleKey} />
      <Box paddingLeft={2} flexDirection="column">
        {items.map((item, index) => (
          <ListItem
            key={startIndex + index + 1}
            itemNumber={startIndex + index + 1}
          >
            {match(item, {
              Item: ({ title, description }) => (
                <Box flexDirection="row" gap={1}>
                  <Text>{title}</Text>
                  {description && <Text skin="secondary">({description})</Text>}
                </Box>
              ),
              ViewMore: () => (
                <Trans i18nKey="general.numeric_viewer.show_more" />
              ),
            })}
          </ListItem>
        ))}
      </Box>
    </Box>
  );
}
